import React from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

const StyledSwitch = styled(Switch)(({ theme }) => ({
	width: 30,
	height: 18,
	padding: 0,
	display: 'flex',
	'& .MuiSwitch-switchBase': {
		padding: 2,
		'&.Mui-checked': {
			transform: 'translateX(14px)',
			color: '#fff',
			'& + .MuiSwitch-track': {
				opacity: 1,
				backgroundColor: '#B1282E',
			},
		},
		'&.Mui-disabled': {
			color: '#ccc !important',
			'& + .MuiSwitch-track': {
				opacity: 0.4,
				backgroundColor: '#444 !important',
			},
		},
	},
	'& .MuiSwitch-thumb': {
		boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
		width: 12,
		height: 14,
		borderRadius: 2,
		transition: theme.transitions.create(['width'], {
			duration: 200,
		}),
	},
	'& .MuiSwitch-track': {
		borderRadius: 4,
		opacity: 1,
		backgroundColor: '#0F1820',
		boxSizing: 'border-box',
	},
}));

/**
 * DataViewSwitch Component
 * Renders a switch component for toggling between two options (e.g., Report View vs Graph View)
 * 
 * @param {boolean} checked - Whether the switch is in the "checked" state (right option)
 * @param {function} onChange - Handler function called when switch state changes (required)
 * @param {string} leftText - Text label for the left option (optional)
 * @param {string} rightText - Text label for the right option (optional)
 * @param {string} leftSwitchOverride - Custom tooltip text for while on the left option (optional)
 * @param {string} rightSwitchOverride - Custom tooltip text for while on the right option (optional)
 * @param {boolean} disabled - Whether the switch is disabled (optional)
 * @param {string} disabledText - Tooltip text when the switch is disabled (optional)
 * @returns {JSX.Element} The rendered DataViewSwitch component
 */
export default function DataViewSwitch({
	checked,
	onChange,
	leftText = 'Report',
	rightText = 'Graph',
	leftSwitchOverride = `Switch to ${ rightText } View`,
	rightSwitchOverride = `Switch to ${ leftText } View`,
	disabled = false,
	disabledText = 'View Switch disabled',
}) {
	const tooltipText = disabled ? (disabledText || '') : (checked ? rightSwitchOverride : leftSwitchOverride);
	const stackOpacity = disabled ? 0.5 : 1;
	const stackCursor = disabled ? 'not-allowed' : 'default';

	const styledText = (text) => (
		<Typography fontFamily='Saira, sans-serif' sx={{ color: disabled ? '#888' : 'inherit' }}>
			{text}
		</Typography>
	);

	return (
		<Box sx={{ display: 'inline-block' }}>
			<Tooltip title={tooltipText}>
				<Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: stackOpacity, cursor: stackCursor }}>
					{ leftText && styledText(leftText) }
					<StyledSwitch checked={checked} onChange={onChange} disabled={disabled} />
					{ rightText && styledText(rightText) }
				</Stack>
			</Tooltip>
		</Box>
	);
}
